/**
 * DeSubjectBaseline — Section-8 fallback for DAAD-sourced German
 * bachelor programmes that lack per-course outcome data (which is all
 * of them — Germany doesn't publish outcomes at that grain federally).
 *
 * Renders the national baseline for the row's subject_area, with clear
 * "national baseline, not degree-specific" framing + verified source
 * link. Never invents numbers; every entry cites a public source.
 */
import { Card } from "@/components/ui/card";
import { ExternalLink, TrendingUp } from "lucide-react";
import { getDeBaseline } from "@/data/deSubjectBaselines";

export function DeSubjectBaseline({ degree }) {
  if (degree?.source_provenance?.source !== "daad_intl_programmes") return null;
  if (degree?.outcomes?.employment_rate) return null;   // real data already
  const subject = degree?.subject_area;
  const b = getDeBaseline(subject);
  if (!b) return null;

  const eur = (n) => `€${n.toLocaleString("en-GB")}`;
  const pct = (n) => `${Math.round(n * 100)}%`;
  const employment = 1 - b.unemployment_rate;

  return (
    <Card
      className="mt-4 p-4 border-stone-300 bg-white"
      data-testid="de-subject-baseline"
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-stone-600" />
        <div className="font-underground-sc text-[10px] tracking-wider text-stone-600">
          Germany national baseline · {subject || "All fields"}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-stone-800">
        <div><b>Median starting salary:</b> {eur(b.median_starting_salary_eur)}/yr</div>
        <div><b>Median salary after 5y:</b> {eur(b.median_after_5y_eur)}/yr</div>
        <div><b>Graduate employment rate:</b> {pct(employment)}</div>
        <div><b>Graduate unemployment:</b> {pct(b.unemployment_rate)}</div>
      </div>
      {b.notes && (
        <div className="mt-3 text-xs text-stone-600 italic">{b.notes}</div>
      )}
      <div className="mt-3 text-[11px] font-mono text-stone-500 flex items-center gap-1">
        <span>National average · not degree-specific ·</span>
        <a
          href={b.source_url}
          target="_blank"
          rel="noreferrer"
          className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
        >
          {b.source_name} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </Card>
  );
}

export default DeSubjectBaseline;
